import { useState } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  TextField,
  InputAdornment,
  Autocomplete,
  Typography,
} from '@material-ui/core';
import { api } from 'src/service';
import { useToast } from 'src/contexts/toast';

const TRANSFER_TYPES = [
  { id: 1, label: 'IBAN' },
  { id: 2, label: 'MBWAY' },
  { id: 3, label: 'Compensation' },
  { id: 4, label: 'Penalty' },
  { id: 5, label: 'Washer Kit Deduction' },
];

const formatLocalDate = (date) => {
  const y = date.getFullYear();
  const m = `${date.getMonth() + 1}`.padStart(2, '0');
  const d = `${date.getDate()}`.padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const getCurrentWeekRangeDescription = () => {
  const today = new Date();
  const start = new Date(today);
  const dow = today.getDay() || 7;
  start.setDate(today.getDate() - dow + 1);
  return `Payment week of ${formatLocalDate(start)} to ${formatLocalDate(today)}`;
};

const getDefaultDescription = (movementType) => {
  if (movementType === 'Washer Kit Deduction') return 'Washer kit';
  if (movementType === 'IBAN' || movementType === 'MBWAY') return getCurrentWeekRangeDescription();
  return '';
};

const WasherAddMovementCard = ({ washerId, onAdded }) => {
  const { showToast } = useToast();
  const [type, setType] = useState(TRANSFER_TYPES[0]);

  const handleSubmit = async (values, { resetForm, setSubmitting }) => {
    try {
      const payload = {
        washer_id: washerId,
        description: values.description,
        payment_amount: values.payment_amount,
        type: type.label,
      };
      await api.post('/paymentwasher', payload);
      resetForm({ values: { description: getDefaultDescription(type.label), payment_amount: '' } });
      showToast('Movement created successfully!');
      if (onAdded) onAdded();
    } catch (err) {
      showToast(err.message, 'warning');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader title="Add Movement" />
      <CardContent>
        <Formik
          enableReinitialize
          initialValues={{
            description: getDefaultDescription(type.label),
            payment_amount: '',
          }}
          validationSchema={Yup.object().shape({
            description: Yup.string().max(255),
            payment_amount: Yup.number()
              .notOneOf([0], 'Amount cannot be zero')
              .required('Amount is required'),
          })}
          onSubmit={handleSubmit}
        >
          {({
            errors,
            handleBlur,
            handleChange,
            handleSubmit: submit,
            setFieldValue,
            touched,
            values,
            isSubmitting,
          }) => (
            <form onSubmit={submit}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    error={Boolean(touched.description && errors.description)}
                    helperText={touched.description && errors.description}
                    fullWidth
                    label="Description"
                    name="description"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.description}
                    variant="outlined"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    error={Boolean(touched.payment_amount && errors.payment_amount)}
                    helperText={touched.payment_amount && errors.payment_amount}
                    fullWidth
                    type="number"
                    label="Amount"
                    name="payment_amount"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.payment_amount}
                    variant="outlined"
                    size="small"
                    InputProps={{
                      endAdornment: <InputAdornment position="end">€</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    options={TRANSFER_TYPES}
                    autoHighlight
                    value={type}
                    getOptionLabel={(opt) => opt.label}
                    onChange={(_, value) => {
                      if (value) {
                        setType(value);
                        setFieldValue('description', getDefaultDescription(value.label));
                      }
                    }}
                    renderInput={(p) => (
                      <TextField
                        {...p}
                        label="Movement Type"
                        variant="outlined"
                        size="small"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">
                    Positive values reduce washer balance. Negative values increase it.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Box>
                    <Button
                      color="primary"
                      variant="contained"
                      fullWidth
                      type="submit"
                      disabled={isSubmitting}
                    >
                      Add
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          )}
        </Formik>
      </CardContent>
    </Card>
  );
};

WasherAddMovementCard.propTypes = {
  washerId: PropTypes.string.isRequired,
  onAdded: PropTypes.func,
};

export default WasherAddMovementCard;
